// ── Integrations Tab ─────────────────────────────────────────
// MCP-style integration hub: connect/disconnect services, manage tool permissions.

import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../api';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Tool definitions per integration ─────────────────────────
// These define what capabilities each integration exposes to the AI.
const INTEGRATION_TOOLS = {
  gmail: [
    { id: 'searchThreads', label: 'Search Threads', description: 'Search email threads by query' },
    { id: 'searchEmail', label: 'Search Email', description: 'Search emails from chat and load threads' },
    { id: 'sendEmail', label: 'Send Email', description: 'Send an email to a recipient' },
    { id: 'createDraft', label: 'Create Draft', description: 'Create a draft email without sending' },
    { id: 'replyToThread', label: 'Reply to Thread', description: 'Reply to an existing email thread' },
    { id: 'exportToGoogleDoc', label: 'Export to Google Doc', description: 'Create a Google Doc from chat content' },
  ],
  github: [
    { id: 'listRepos', label: 'List Repos', description: 'List and search GitHub repositories' },
    { id: 'analyzeRepo', label: 'Analyze Repo', description: 'Analyze a GitHub repository codebase' },
  ],
  'claude-code': [
    { id: 'importContext', label: 'Import Context', description: 'Import Claude Code session context' },
  ],
};

// ── Icons (simple text icons matching the app style) ─────────
const INTEGRATION_ICONS = {
  gmail: '✉',
  github: '⟨/⟩',
  'google-docs': '📄',
  'claude-code': '◇',
};

// ── Integration Card Component ───────────────────────────────
function IntegrationCard({ integration, onConnect, onDisconnect, disabledTools, onToggleTool }) {
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showTools, setShowTools] = useState(integration.connected);

  const icon = INTEGRATION_ICONS[integration.id] || '⬡';
  const tools = INTEGRATION_TOOLS[integration.id] || [];

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await onConnect(integration.id);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Disconnect ${integration.name}? The AI will lose access to ${integration.name} tools.`)) return;
    setDisconnecting(true);
    try {
      await onDisconnect(integration.id);
    } finally {
      setDisconnecting(false);
    }
  };

  const statusBadge = integration.connected
    ? 'intg-status--connected'
    : integration.configured
      ? 'intg-status--disconnected'
      : 'intg-status--unconfigured';

  const statusLabel = integration.connected
    ? 'Connected'
    : integration.configured
      ? 'Disconnected'
      : 'Not Configured';

  return (
    <div className={`intg-card ${integration.connected ? 'intg-card--connected' : ''}`}>
      <div className="intg-card-header">
        <div className="intg-card-identity">
          <span className="intg-card-icon">{icon}</span>
          <div>
            <div className="intg-card-name">{integration.name}</div>
            {integration.description && (
              <div className="intg-card-desc">{integration.description}</div>
            )}
          </div>
        </div>
        <div className="intg-card-actions">
          <span className={`intg-status-badge ${statusBadge}`}>{statusLabel}</span>
          {integration.connected ? (
            <button
              className="intg-btn intg-btn--disconnect"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
          ) : integration.configured ? (
            <button
              className="intg-btn intg-btn--connect"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? 'Connecting...' : 'Connect'}
            </button>
          ) : (
            <button className="intg-btn intg-btn--disabled" disabled>
              Configure Server
            </button>
          )}
        </div>
      </div>

      {/* Connected account */}
      {integration.connected && integration.account && (
        <div className="intg-account">
          Signed in as <strong>{integration.account}</strong>
        </div>
      )}

      {/* Tools section */}
      {integration.connected && tools.length > 0 && (
        <div className="intg-tools">
          <button
            className="intg-tools-header"
            onClick={() => setShowTools(v => !v)}
          >
            <span className="intg-tools-title">
              Tools
              <span className="intg-tools-count">{tools.length}</span>
            </span>
            <span className={`intg-tools-chevron ${showTools ? 'intg-tools-chevron--open' : ''}`}>›</span>
          </button>
          {showTools && (
            <div className="intg-tools-list">
              {tools.map(tool => {
                const isDisabled = disabledTools.includes(tool.id);
                return (
                  <div key={tool.id} className="intg-tool-row">
                    <label className="intg-tool-toggle">
                      <input
                        type="checkbox"
                        checked={!isDisabled}
                        onChange={() => onToggleTool(integration.id, tool.id)}
                      />
                      <span className="intg-tool-slider" />
                    </label>
                    <div className="intg-tool-info">
                      <span className="intg-tool-label">{tool.label}</span>
                      <span className="intg-tool-desc">{tool.description}</span>
                    </div>
                  </div>
                );
              })}
              <div className="intg-tools-bulk">
                <button
                  className="intg-btn-text"
                  onClick={() => tools.forEach(t => {
                    if (disabledTools.includes(t.id)) onToggleTool(integration.id, t.id);
                  })}
                >
                  Enable All
                </button>
                <button
                  className="intg-btn-text"
                  onClick={() => tools.forEach(t => {
                    if (!disabledTools.includes(t.id)) onToggleTool(integration.id, t.id);
                  })}
                >
                  Disable All
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Not configured hint */}
      {!integration.configured && (
        <div className="intg-config-hint">
          Requires server configuration. Check environment variables for this integration.
        </div>
      )}
    </div>
  );
}

// ── Main Tab Component ───────────────────────────────────────
export default function IntegrationsTab() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disabledTools, setDisabledTools] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('intg_disabled_tools') || '{}');
    } catch { return {}; }
  });

  // Fetch all integration statuses
  const fetchIntegrations = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/integrations`);
      const data = await res.json();
      setIntegrations(data.integrations || data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load integrations');
      console.error('Failed to fetch integrations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // Persist disabled tools
  useEffect(() => {
    localStorage.setItem('intg_disabled_tools', JSON.stringify(disabledTools));
  }, [disabledTools]);

  // Listen for OAuth popup callbacks
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'gmail-connected' || e.data?.type === 'github-connected') {
        fetchIntegrations();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchIntegrations]);

  // Connect an integration (open OAuth popup)
  const handleConnect = useCallback(async (id) => {
    try {
      const res = await authFetch(`${API_URL}/api/integrations/${id}/connect`, { method: 'POST' });
      const data = await res.json();
      if (data.authUrl) {
        const w = 500, h = 600;
        const left = window.screenX + (window.innerWidth - w) / 2;
        const top = window.screenY + (window.innerHeight - h) / 2;
        window.open(data.authUrl, `${id}-oauth`, `width=${w},height=${h},left=${left},top=${top}`);
      } else {
        // Non-OAuth integration (e.g. claude-code) — just refresh
        await fetchIntegrations();
      }
    } catch (err) {
      console.error(`Failed to connect ${id}:`, err);
    }
  }, [fetchIntegrations]);

  // Disconnect an integration
  const handleDisconnect = useCallback(async (id) => {
    try {
      await authFetch(`${API_URL}/api/integrations/${id}/disconnect`, { method: 'POST' });
      await fetchIntegrations();
    } catch (err) {
      console.error(`Failed to disconnect ${id}:`, err);
    }
  }, [fetchIntegrations]);

  // Toggle a tool's enabled state
  const handleToggleTool = useCallback((integrationId, toolId) => {
    setDisabledTools(prev => {
      const intgTools = prev[integrationId] || [];
      const isDisabled = intgTools.includes(toolId);
      const updated = isDisabled
        ? intgTools.filter(t => t !== toolId)
        : [...intgTools, toolId];
      return { ...prev, [integrationId]: updated };
    });
  }, []);

  if (loading) {
    return (
      <div className="settings-section">
        <h2 className="settings-section-title">Integrations</h2>
        <div className="settings-card" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          Loading integrations...
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">Integrations</h2>
      <p className="intg-subtitle">
        Connect services to give the AI access to external tools. Each integration exposes capabilities that the AI can invoke from chat.
      </p>

      {error && (
        <div className="intg-error">{error}</div>
      )}

      <div className="intg-grid">
        {integrations.map(intg => (
          <IntegrationCard
            key={intg.id}
            integration={intg}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            disabledTools={disabledTools[intg.id] || []}
            onToggleTool={handleToggleTool}
          />
        ))}
      </div>

      {integrations.length === 0 && !error && (
        <div className="settings-card" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          No integrations registered. Check server configuration.
        </div>
      )}
    </div>
  );
}
